import { appendDisplayCacheBust, invalidateManagedDisplayCache } from '@/utils/imageDisplayAccess';
import { unmarkManagedViewImageLoaded } from '@/utils/managedViewImageLoadCache';
import { mapCropRowToDetailRow } from './imageCropRowStatus';

function trimOrEmpty(value) {
  return value == null ? '' : String(value).trim();
}

function readPrintIndex(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function normalizePrintList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const printIndex = readPrintIndex(item.printIndex ?? item.print_index) ?? index;
      return { ...item, printIndex };
    })
    .filter(Boolean);
}

/** 为手动裁剪结果追加展示 revision，避免同路径覆盖后浏览器/签名缓存仍显示旧图 */
export function applyManualCropResultRevision(row, revision = Date.now()) {
  if (!row || typeof row !== 'object') return row;
  const rev = Number(revision) || Date.now();
  const bustToken = String(rev);
  const bustDisplay = (displayUrl) => {
    const preferred = trimOrEmpty(displayUrl);
    if (!preferred) return '';
    // 勿用 appendViewVariant(mc*)：normalizeImageViewVariant 只认 thumb/preview/medium，会原样返回
    return appendDisplayCacheBust(preferred, bustToken);
  };

  const next = { ...row, resultRevision: rev };
  const bustedPrimary = bustDisplay(next.croppedDisplayUrl);
  if (bustedPrimary) {
    next.croppedDisplayUrl = bustedPrimary;
  }

  const prints = Array.isArray(next.cropPosition?.prints) ? next.cropPosition.prints : [];
  if (prints.length > 0) {
    next.cropPosition = {
      ...next.cropPosition,
      prints: prints.map((print) => {
        const croppedImageUrlDisplay = trimOrEmpty(print.croppedImageUrlDisplay);
        const busted = bustDisplay(croppedImageUrlDisplay);
        return busted
          ? { ...print, croppedImageUrlDisplay: busted }
          : print;
      }),
    };
  }

  return next;
}

/** 清理受管读图缓存，确保后续重新签名/拉取最新文件 */
export function invalidateManualCropDisplayCache(row, { printIndex = null } = {}) {
  if (!row) return;
  const imageId = trimOrEmpty(row.imageId);
  const invalidateOne = (canonicalUrl, displayUrl, id = imageId) => {
    const raw = trimOrEmpty(canonicalUrl);
    const display = trimOrEmpty(displayUrl);
    if (raw) {
      invalidateManagedDisplayCache(raw, { imageId: id });
      unmarkManagedViewImageLoaded(raw);
    }
    if (display && display !== raw) {
      invalidateManagedDisplayCache(display, { imageId: id });
      unmarkManagedViewImageLoaded(display);
    }
  };

  invalidateOne(row.croppedUrl, row.croppedDisplayUrl, imageId);

  const prints = Array.isArray(row.cropPosition?.prints) ? row.cropPosition.prints : [];
  prints.forEach((print) => {
    const idx = readPrintIndex(print?.printIndex);
    if (printIndex != null && idx != null && idx !== printIndex) return;
    const tileId = idx != null && prints.length > 1
      ? `${imageId}-print-${idx}`
      : imageId;
    invalidateOne(print?.croppedImageUrl, print?.croppedImageUrlDisplay, tileId);
  });
}

/**
 * 手动裁剪后合并详情行：多印花时仅更新指定 printIndex，保留其余印花裁剪结果。
 *
 * @param {object} row 当前详情行
 * @param {object} apiRow crop-by-bbox 接口返回
 * @param {{ printIndex?: number | null }} [options]
 */
export function mergeManualCropIntoDetailRow(row, apiRow, options = {}) {
  if (!row || !apiRow) return row;

  const mapped = mapCropRowToDetailRow(
    row.imageId,
    {
      imageId: row.imageId,
      status: trimOrEmpty(apiRow.status) || 'success',
      croppedImageUrl: trimOrEmpty(apiRow.croppedImageUrl ?? apiRow.cropped_image_url),
      croppedImageUrlDisplay: trimOrEmpty(
        apiRow.croppedImageUrlDisplay
        ?? apiRow.cropped_image_url_display
        ?? apiRow.viewUrl,
      ),
      originalImageUrl: row.originalUrl,
      error: apiRow.error || null,
      pipeline: apiRow.pipeline || 'crop-by-bbox-v1',
      usedFallback: Boolean(apiRow.usedFallback),
      cropPosition: apiRow.cropPosition ?? row.cropPosition,
    },
    row.originalUrl,
  );

  const targetPrintIndex = readPrintIndex(options.printIndex);
  if (targetPrintIndex == null) {
    return applyManualCropResultRevision(
      { ...row, ...mapped },
      options.revision,
    );
  }

  const previousPrints = normalizePrintList(row.cropPosition?.prints);
  const nextPrints = normalizePrintList(mapped.cropPosition?.prints);
  if (previousPrints.length <= 1 && nextPrints.length <= 1) {
    return applyManualCropResultRevision(
      { ...row, ...mapped },
      options.revision,
    );
  }

  const mergedPrints = previousPrints.map((print) => ({ ...print }));
  nextPrints.forEach((print) => {
    const idx = mergedPrints.findIndex(
      (item) => Number(item.printIndex) === Number(print.printIndex),
    );
    if (idx >= 0) {
      mergedPrints[idx] = { ...mergedPrints[idx], ...print };
    } else {
      mergedPrints.push(print);
    }
  });
  mergedPrints.sort((a, b) => Number(a.printIndex) - Number(b.printIndex));

  const cropPosition = {
    ...(mapped.cropPosition || row.cropPosition || {}),
    prints: mergedPrints,
    printCount: mergedPrints.length,
    multiPrint: mergedPrints.length > 1,
    manual: true,
  };

  return applyManualCropResultRevision({
    ...row,
    ...mapped,
    cropPosition,
    status: mergedPrints.length > 1 ? (mapped.status === 'failed' ? 'failed' : 'success') : mapped.status,
  }, options.revision);
}
